import { cloneElement, Fragment } from 'react';
import { connect } from 'react-redux';
import type { ServiceUrlObject } from '@common/interfaces/serviceUrlObject';
import type { IProcess, IProcessDiagnosticReport } from '@frontend/components/interfaces/process';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization } from '@common/enums';
import { ProcessScrollItem } from '@common/enums/processScrollItem';
import { ReportCategories } from '@common/enums/reportCategories';
import { ReportStatus } from '@common/enums/reportStatus';
import { RequestStatus } from '@common/enums/requestStatus';
import { ScheduledRequestStatus } from '@common/enums/scheduledRequestStatus';
import { ServiceCode } from '@common/enums/serviceCode';
import { ServiceType } from '@common/enums/serviceType';
import { SupervisionStatus } from '@common/enums/supervisionStatus';
import { serviceUsesBioGraders } from '@common/utils/serviceUsesBioGraders';
import { Component } from '@frontend/components/elements/Component';
import { hasOneAuthorization } from '@frontend/utils';
import type { IProcessItemState, IReportItemState } from './Episode';
import { ClarificationItem } from '../diagnosticReports/ClarificationItem';
import { DiagnosticReportItem } from '../diagnosticReports/DiagnosticReportItem';
import { ProcedureItem } from '../procedures/ProcedureItem';
import { ScheduledRequestItem } from '../procedures/ScheduledRequestItem';
import { RequestItem } from '../requests/RequestItem';
import { SupervisionItem, SupervisionMode } from '../supervisions/SupervisionItem';
import { CollapseWrapper } from './common';
import { ReloadEpisode } from './reloadEpisode';

interface IProcessProps {
    disableFormAutosave: boolean;
    isPreviousRequest: boolean;
    onLoaded: () => void;
    onReloadPatient?: () => Promise<void>;
    onServiceIdSelected?: (args: ServiceUrlObject) => Promise<void>;
    process: IProcess;
    processItemState: IProcessItemState;
    reloadSelfOrAllProcesses: (reload: ReloadEpisode, requestId: string) => Promise<void>;
    removeProcess: (requestId: string) => Promise<void>;
    scrollToProcessItem: (requestId: string, scrollItem: ProcessScrollItem) => Promise<void>;
    setHideCreateActions: (value: boolean) => Promise<void>;
    updateProcessItemState: (requestId: string, processItemState: IProcessItemState) => Promise<void>;
}

interface IProcessReduxProps {
    organizationId: string;
    practitionerId: string;
}

const mapStateToProps = ({ authentication: { id, organizationId } }: IReduxState): IProcessReduxProps => ({
    practitionerId: id,
    organizationId,
});

class WrapperProcess extends Component<IProcessProps & IProcessReduxProps, {}> {
    private initialItemsToBeLoaded: number;

    public async componentDidUpdate(prevProps: Readonly<IProcessProps & IProcessReduxProps>) {
        // This is needed when the service of a consult is being changed for the first time when adding a consult diagnosticReport and has not been saved as a draft.
        // The addEdit of the diagnosticReport is then remounted and needs to be changed back to 'edit' mode (ChangeService functionality)
        if (
            prevProps.process.diagnosticReports.length === 0 &&
            this.props.process.serviceId !== prevProps.process.serviceId &&
            this.props.process.diagnosticReports.length === 1 &&
            this.props.process.diagnosticReports[0].category === ReportCategories.consultReport
        ) {
            const diagnosticReportItemState = this.props.processItemState.diagnosticReports.get(
                this.props.process.diagnosticReports[0].id
            );
            if (diagnosticReportItemState) {
                await this.props.updateProcessItemState(this.props.process.id, {
                    ...this.props.processItemState,
                    diagnosticReports: this.props.processItemState.diagnosticReports.set(
                        this.props.process.diagnosticReports[0].id,
                        { ...diagnosticReportItemState, mode: 'edit' }
                    ),
                });
            }
        }
    }

    public render() {
        const processFormId = 'processForm' + this.props.process.id;
        const processElements = this.getProcessElements(processFormId, this.props.disableFormAutosave);
        if (this.initialItemsToBeLoaded === undefined) {
            this.initialItemsToBeLoaded = processElements.length;
        }
        let isFirstItem = true;

        return (
            <CollapseWrapper
                id={this.props.process.id}
                isPreviousRequest={this.props.isPreviousRequest}
                processFormId={processFormId}
            >
                {processElements.map((element, index) => {
                    let renderElement = element;
                    if (isFirstItem) {
                        renderElement = cloneElement(element, { isFirstItem: true });
                    }
                    isFirstItem = false;

                    return <Fragment key={`processItem_${this.props.process.id}_${index}`}>{renderElement}</Fragment>;
                })}
            </CollapseWrapper>
        );
    }

    private getProcessElements(processFormId: string, disableFormAutosave: boolean) {
        const organizationIsPerformerProcedureAndReport = this.isPerformerProcedureAndReport();
        const elements: JSX.Element[] = [];
        let scheduledRequestItemAdded = false;

        if (this.props.process.scheduledRequest?.status === ScheduledRequestStatus.scheduled) {
            elements.unshift(<ScheduledRequestItem process={this.props.process} />);
            scheduledRequestItemAdded = true;
        }

        if (this.showSupervisionGrading()) {
            elements.unshift(
                this.supervisionProcessContainer(
                    this.isLastItem(elements, scheduledRequestItemAdded),
                    processFormId,
                    SupervisionMode.showGrading
                )
            );
        }

        if (this.showSupervisionBiometrics()) {
            elements.unshift(
                this.supervisionProcessContainer(
                    this.isLastItem(elements, scheduledRequestItemAdded),
                    processFormId,
                    SupervisionMode.showBiometrics
                )
            );
        }

        if (this.showSupervisionDraft()) {
            elements.unshift(
                this.supervisionProcessContainer(
                    this.isLastItem(elements, scheduledRequestItemAdded),
                    processFormId,
                    SupervisionMode.showGrading
                )
            );
            elements.unshift(
                this.supervisionProcessContainer(
                    this.isLastItem(elements, scheduledRequestItemAdded),
                    processFormId,
                    SupervisionMode.showBiometrics
                )
            );
        }

        if (this.showSupervisionEdit()) {
            elements.unshift(
                this.supervisionProcessContainer(this.isLastItem(elements, scheduledRequestItemAdded), processFormId)
            );
        }

        if (this.showDiagnosticReportAdd()) {
            elements.unshift(
                <DiagnosticReportItem
                    cancelAddDiagnosticReport={async () =>
                        await this.props.updateProcessItemState(this.props.process.id, {
                            ...this.props.processItemState,
                            newReportCategory: undefined,
                        })
                    }
                    disableFormAutosave={this.props.disableFormAutosave}
                    formId={processFormId}
                    isFirstItem={false}
                    isLastItem={this.isLastItem(elements, scheduledRequestItemAdded)}
                    isPreviousRequest={this.props.isPreviousRequest}
                    itemState={{ isOpen: true, addClarification: false, mode: 'add', isClarificationOpen: false }}
                    onLoaded={() => this.setItemLoaded()}
                    process={this.props.process}
                    reloadSelfOrAllProcesses={async (reload) => {
                        await this.props.updateProcessItemState(this.props.process.id, {
                            ...this.props.processItemState,
                            newReportCategory: undefined,
                        });
                        await this.props.reloadSelfOrAllProcesses(reload, this.props.process.id);
                    }}
                    reportCategory={this.props.processItemState.newReportCategory}
                    scrollToProcessItem={this.props.scrollToProcessItem}
                    setHideCreateActions={this.props.setHideCreateActions}
                />
            );
        }

        if (this.showDiagnosticReport(organizationIsPerformerProcedureAndReport)) {
            for (const diagnosticReport of this.props.process.diagnosticReports.slice().reverse()) {
                const diagnosticReportItemState = this.props.processItemState.diagnosticReports.get(
                    diagnosticReport.id
                );
                if (!diagnosticReportItemState) {
                    throw new Error('Missing report item state for reportId: ' + diagnosticReport.id);
                }

                if (this.showDiagnosticReportClarifications(diagnosticReport, diagnosticReportItemState)) {
                    elements.unshift(
                        <ClarificationItem
                            diagnosticReport={diagnosticReport}
                            isLastItem={this.isLastItem(elements, scheduledRequestItemAdded)}
                            isPreviousRequest={this.props.isPreviousRequest}
                            itemState={diagnosticReportItemState}
                            onLoaded={() => this.setItemLoaded()}
                            process={this.props.process}
                            reloadSelfOrAllProcesses={async (reload) =>
                                await this.props.reloadSelfOrAllProcesses(reload, this.props.process.id)
                            }
                            updateItemState={async (itemState) =>
                                await this.props.updateProcessItemState(this.props.process.id, {
                                    ...this.props.processItemState,
                                    diagnosticReports: this.props.processItemState.diagnosticReports.set(
                                        diagnosticReport.id,
                                        itemState
                                    ),
                                })
                            }
                        />
                    );
                }

                elements.unshift(
                    <DiagnosticReportItem
                        addDiagnosticReport={async (reportCategory: ReportCategories) => {
                            if (this.props.processItemState.newReportCategory !== reportCategory) {
                                await this.props.updateProcessItemState(this.props.process.id, {
                                    ...this.props.processItemState,
                                    newReportCategory: reportCategory,
                                });
                                await this.props.scrollToProcessItem(
                                    this.props.process.id,
                                    ProcessScrollItem.diagnosticReport
                                );
                            }
                        }}
                        cancelAddDiagnosticReport={async () =>
                            await this.props.updateProcessItemState(this.props.process.id, {
                                ...this.props.processItemState,
                                newReportCategory: undefined,
                            })
                        }
                        diagnosticReport={diagnosticReport}
                        disableFormAutosave={this.props.disableFormAutosave}
                        formId={processFormId}
                        isFirstItem={false}
                        isLastItem={this.isLastItem(elements, scheduledRequestItemAdded)}
                        isPreviousRequest={this.props.isPreviousRequest}
                        itemState={diagnosticReportItemState}
                        onLoaded={() => this.setItemLoaded()}
                        process={this.props.process}
                        reloadSelfOrAllProcesses={async (reload) =>
                            await this.props.reloadSelfOrAllProcesses(reload, this.props.process.id)
                        }
                        scrollToProcessItem={this.props.scrollToProcessItem}
                        setHideCreateActions={this.props.setHideCreateActions}
                        updateItemState={async (itemState) =>
                            await this.props.updateProcessItemState(this.props.process.id, {
                                ...this.props.processItemState,
                                diagnosticReports: this.props.processItemState.diagnosticReports.set(
                                    diagnosticReport.id,
                                    itemState
                                ),
                            })
                        }
                        updateSupervision={
                            this.props.processItemState.supervision
                                ? undefined
                                : async () => {
                                      await this.props.updateProcessItemState(this.props.process.id, {
                                          ...this.props.processItemState,
                                          supervision: { isOpen: true, isGradingOpen: true, mode: 'edit' },
                                      });
                                      await this.props.scrollToProcessItem(
                                          this.props.process.id,
                                          ProcessScrollItem.supervision
                                      );
                                  }
                        }
                    />
                );
            }
        }

        if (this.showProcedure() || this.showProcedureAdd()) {
            if (!this.props.processItemState.procedure) {
                throw new Error('Missing procedure item state for procedure requestId: ' + this.props.process.id);
            }

            elements.unshift(
                <ProcedureItem
                    addExaminationReport={
                        this.props.processItemState.newReportCategory &&
                        this.props.processItemState.newReportCategory === ReportCategories.examinationReport
                            ? undefined
                            : async () => {
                                  await this.props.updateProcessItemState(this.props.process.id, {
                                      ...this.props.processItemState,
                                      newReportCategory: ReportCategories.examinationReport,
                                  });
                                  await this.props.scrollToProcessItem(
                                      this.props.process.id,
                                      ProcessScrollItem.diagnosticReport
                                  );
                              }
                    }
                    disableFormAutosave={disableFormAutosave}
                    formId={processFormId}
                    isBiometrist={this.isBiometrist()}
                    isFirstItem={false}
                    isLastItem={this.isLastItem(elements, scheduledRequestItemAdded)}
                    isPreviousRequest={this.props.isPreviousRequest}
                    itemState={this.props.processItemState.procedure}
                    onLoaded={() => this.setItemLoaded()}
                    onReloadPatient={this.props.onReloadPatient}
                    onServiceIdSelected={this.props.onServiceIdSelected}
                    organizationIsPerformerProcedureAndReport={organizationIsPerformerProcedureAndReport}
                    process={this.props.process}
                    reloadSelfOrAllProcesses={async (reload) =>
                        await this.props.reloadSelfOrAllProcesses(reload, this.props.process.id)
                    }
                    removeProcess={async () => await this.props.removeProcess(this.props.process.id)}
                    reportItemState={this.props.processItemState.diagnosticReports}
                    scrollToProcessItem={this.props.scrollToProcessItem}
                    setHideCreateActions={this.props.setHideCreateActions}
                    updateItemState={async (itemState) =>
                        await this.props.updateProcessItemState(this.props.process.id, {
                            ...this.props.processItemState,
                            procedure: itemState,
                        })
                    }
                    updateReportItemState={async (id, itemState) => {
                        if (itemState) {
                            await this.props.updateProcessItemState(this.props.process.id, {
                                ...this.props.processItemState,
                                diagnosticReports: this.props.processItemState.diagnosticReports.set(id, itemState),
                            });
                        } else {
                            await this.props.updateProcessItemState(this.props.process.id, {
                                ...this.props.processItemState,
                                diagnosticReports: this.props.processItemState.diagnosticReports.remove(id),
                            });
                        }
                    }}
                    updateSupervision={
                        this.props.processItemState.supervision
                            ? undefined
                            : async () => {
                                  await this.props.updateProcessItemState(this.props.process.id, {
                                      ...this.props.processItemState,
                                      supervision: { isOpen: true, isGradingOpen: true, mode: 'edit' },
                                  });
                                  await this.props.scrollToProcessItem(
                                      this.props.process.id,
                                      ProcessScrollItem.supervision
                                  );
                              }
                    }
                />
            );
        }

        if (this.showRequest()) {
            elements.unshift(
                <RequestItem
                    addProcedure={
                        this.props.processItemState.procedure
                            ? undefined
                            : async () => {
                                  await this.props.updateProcessItemState(this.props.process.id, {
                                      ...this.props.processItemState,
                                      procedure: { isOpen: true, mode: 'add' },
                                  });
                                  await this.props.scrollToProcessItem(
                                      this.props.process.id,
                                      ProcessScrollItem.procedure
                                  );
                              }
                    }
                    addReport={
                        this.props.processItemState.newReportCategory &&
                        this.props.processItemState.newReportCategory === ReportCategories.consultReport
                            ? undefined
                            : async (reportCategory: ReportCategories) => {
                                  if (this.props.processItemState.newReportCategory !== reportCategory) {
                                      await this.props.updateProcessItemState(this.props.process.id, {
                                          ...this.props.processItemState,
                                          newReportCategory: reportCategory,
                                      });
                                      await this.props.scrollToProcessItem(
                                          this.props.process.id,
                                          ProcessScrollItem.diagnosticReport
                                      );
                                  }
                              }
                    }
                    disableFormAutosave={this.props.disableFormAutosave}
                    formId={processFormId}
                    isFirstItem={false}
                    isLastItem={this.isLastItem(elements, scheduledRequestItemAdded)}
                    itemState={this.props.processItemState.request}
                    onLoaded={() => this.setItemLoaded()}
                    onReloadPatient={this.props.onReloadPatient}
                    process={this.props.process}
                    reloadProcess={async (reload?: ReloadEpisode) =>
                        await this.props.reloadSelfOrAllProcesses(
                            reload || ReloadEpisode.reloadSelf,
                            this.props.process.id
                        )
                    }
                    removeProcess={async (requestId?: string) =>
                        await this.props.removeProcess(requestId || this.props.process.id)
                    }
                    scrollToProcessItem={this.props.scrollToProcessItem}
                    setHideCreateActions={this.props.setHideCreateActions}
                    updateItemState={async (itemState) =>
                        await this.props.updateProcessItemState(this.props.process.id, {
                            ...this.props.processItemState,
                            request: itemState,
                        })
                    }
                    updateProcessItemStateByRequestId={async (requestId, itemState, scrollItem) => {
                        await this.props.updateProcessItemState(requestId, {
                            ...this.props.processItemState,
                            request: itemState,
                        });
                        await this.props.scrollToProcessItem(requestId, scrollItem);
                    }}
                />
            );
        }

        return elements;
    }

    private supervisionProcessContainer(isLastItem: boolean, processFormId: string, mode?: SupervisionMode) {
        if (!this.props.processItemState.supervision) {
            if (this.props.process.code === ServiceCode.TOO) {
                return <></>;
            }
            throw new Error('Missing supervision item state');
        }

        return (
            <SupervisionItem
                formId={processFormId}
                isFirstItem={false}
                isLastItem={isLastItem}
                isPreviousRequest={this.props.isPreviousRequest}
                itemState={this.props.processItemState.supervision}
                mode={mode}
                onLoaded={() => this.setItemLoaded()}
                process={this.props.process}
                reloadProcess={async () =>
                    await this.props.reloadSelfOrAllProcesses(ReloadEpisode.reloadSelf, this.props.process.id)
                }
                supervision={this.props.process.supervision!}
                updateItemState={async (itemState) =>
                    await this.props.updateProcessItemState(this.props.process.id, {
                        ...this.props.processItemState,
                        supervision: itemState,
                    })
                }
            />
        );
    }

    private isBiometrist() {
        return (
            this.props.process.procedure &&
            this.props.process.procedure.performerPractitionerId === this.props.practitionerId
        );
    }

    private isGrader() {
        return this.props.process.diagnosticReports[0].performerPractitionerId === this.props.practitionerId;
    }

    private isInSupervisorOrganization() {
        return (
            this.props.process.supervision &&
            this.props.process.supervision.performerOrganizationId === this.props.organizationId
        );
    }

    private isPerformerProcedureAndReport() {
        if (!serviceUsesBioGraders(this.props.process.code)) {
            return false;
        }
        if (
            this.props.process.procedure &&
            this.props.process.diagnosticReports &&
            this.props.process.diagnosticReports[0]
        ) {
            const procedure = this.props.process.procedure;
            const diagnosticReport = this.props.process.diagnosticReports[0];
            if (
                diagnosticReport.performerOrganizationId &&
                diagnosticReport.performerOrganizationId === procedure.performerOrganizationId &&
                diagnosticReport.performerPractitionerId !== undefined &&
                diagnosticReport.performerPractitionerId === procedure.performerPractitionerId
            ) {
                return true;
            }
        }

        return false;
    }

    private showRequest() {
        return !this.props.process.isDirectProcedureAdded;
    }

    private showProcedure() {
        return (
            this.props.process.procedure &&
            this.props.process.serviceType === ServiceType.examination &&
            (this.props.process.status === RequestStatus.active ||
                this.props.process.status === RequestStatus.completed ||
                this.props.process.status === RequestStatus.aborted ||
                this.props.process.status === RequestStatus.draft ||
                this.props.process.status === RequestStatus.cancelled ||
                this.props.process.status === RequestStatus.withAdmin)
        );
    }

    private showProcedureAdd() {
        return this.props.processItemState.procedure && this.props.processItemState.procedure.mode === 'add';
    }

    private showDiagnosticReport(isPerformerProcedureAndReport: boolean) {
        const diagnosticReport = this.props.process.diagnosticReports[0];
        if (
            !diagnosticReport ||
            (diagnosticReport.category === ReportCategories.examinationReport &&
                diagnosticReport.reviewStatus !== ReportStatus.draft &&
                diagnosticReport.gradingSummary === undefined)
        ) {
            return false;
        }

        return !isPerformerProcedureAndReport;
    }

    private showDiagnosticReportAdd() {
        return this.props.processItemState.newReportCategory;
    }

    private showDiagnosticReportClarifications(
        diagnosticReport: IProcessDiagnosticReport,
        reportItemState: IReportItemState
    ) {
        return (
            diagnosticReport.category === ReportCategories.consultReport &&
            ((diagnosticReport.reviewStatus === ReportStatus.open &&
                (diagnosticReport.clarificationQARequester ||
                    (this.props.process.recipientPractitionerId === this.props.practitionerId &&
                        reportItemState.addClarification))) ||
                (diagnosticReport.reviewStatus === ReportStatus.closed && diagnosticReport.clarificationQARequester))
        );
    }

    private showSupervisionEdit() {
        return this.props.processItemState.supervision && this.props.processItemState.supervision.mode === 'edit';
    }

    private showSupervisionDraft() {
        return (
            this.props.process.supervision &&
            this.props.process.supervision.status === SupervisionStatus.draft &&
            this.props.processItemState.supervision &&
            this.props.processItemState.supervision.mode === 'read' &&
            this.isInSupervisorOrganization()
        );
    }

    private showSupervisionGrading() {
        return (
            this.props.process.supervision &&
            this.props.process.supervision.status === SupervisionStatus.completed &&
            (this.isGrader() ||
                this.isInSupervisorOrganization() ||
                hasOneAuthorization(ManagerAuthorization.patientProcesses))
        );
    }

    private showSupervisionBiometrics() {
        return (
            this.props.process.supervision &&
            this.props.process.supervision.status === SupervisionStatus.completed &&
            (this.isBiometrist() ||
                this.isInSupervisorOrganization() ||
                hasOneAuthorization(ManagerAuthorization.patientProcesses)) &&
            !this.isSupervisionForGradingAndBiometrics()
        );
    }

    private setItemLoaded() {
        this.initialItemsToBeLoaded--;
        if (this.initialItemsToBeLoaded === 0) {
            this.props.onLoaded();
        }
    }

    private isLastItem(elements: JSX.Element[], scheduledRequestItemAdded: boolean) {
        return elements.length === 0 || (elements.length === 1 && scheduledRequestItemAdded);
    }

    private isSupervisionForGradingAndBiometrics() {
        return this.props.process.code === ServiceCode.TOO;
    }
}

export const Process = connect(mapStateToProps)(WrapperProcess);
